import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './MicrositeSectionList.module.css'
import { GripVertical, Pencil, Clock } from 'lucide-react'
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch'

const SECTION_LABELS = {
    heroSection: 'Hero banner',
    aboutSection: 'About us',
    categoriesSection: 'Categories',
    testimonialsSection: 'Testimonials',
    contactSection: 'Contact',
    footerSection: 'Footer',
    featuresSection: 'Features',
    ourTeamSection: 'Our team',
    faqSection: 'FAQ',
    notFoundSection: '404 page'
}

/**
 * MicrositeSectionList
 * Drag-to-reorder list of landing page section cards. Each card
 * links to the section editor and has its own visibility toggle.
 *
 * Usage:
 *   <MicrositeSectionList
 *     sections={landingSections}
 *     onReorder={(next) => handleReorder(next)}
 *     onToggleVisible={(sectionKey, value) => handleToggle(sectionKey, value)}
 *   />
 */
const MicrositeSectionList = ({ sections = [], onReorder, onToggleVisible }) => {
    const [dragIdx, setDragIdx] = useState(null)

    const formatDate = (d) => {
        if (!d) return null
        try {
            return new Date(d.replace(' ', 'T')).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
        } catch {
            return null
        }
    }

    const handleDrop = (idx) => {
        if (dragIdx === null || dragIdx === idx) {
            setDragIdx(null)
            return
        }
        const next = [...sections]
        const [moved] = next.splice(dragIdx, 1)
        next.splice(idx, 0, moved)
        onReorder?.(next.map((s, i) => ({ sectionKey: s.sectionKey, sortOrder: i })))
        setDragIdx(null)
    }

    return (
        <div className={styles.list}>
            {sections.map((section, idx) => (
                <div
                    key={section.sectionKey}
                    className={`${styles.card} ${dragIdx === idx ? styles.cardDragging : ''}`}
                    draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(idx)}
                >
                    <GripVertical size={15} className={styles.dragHandle} />

                    <div className={styles.cardInfo}>
                        <div className={styles.cardTitle}>
                            {SECTION_LABELS[section.sectionKey] || section.sectionKey}
                        </div>
                        {section.updatedOn && (
                            <div className={styles.cardMeta}>
                                <Clock size={11} />
                                Updated {formatDate(section.updatedOn)}
                            </div>
                        )}
                    </div>

                    <ToggleSwitch
                        size="sm"
                        checked={!!section.isVisible}
                        onChange={(v) => onToggleVisible?.(section.sectionKey, v)}
                    />

                    <Link to={`/microsite/landing/${section.sectionKey}`} className={styles.editBtn}>
                        <Pencil size={13} />
                        Edit
                    </Link>
                </div>
            ))}
        </div>
    )
}

export default MicrositeSectionList